import React, { useState } from 'react';
import Footer from '../components/Footer';

const MyAccountPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Mock user data
  const userData = {
    name: 'Margaret Johnson',
    email: 'margaret.johnson@email.com',
    phone: '(555) 123-4567',
    address: '123 Oak Street, Springfield, IL 62701',
    emergencyContacts: [
      { name: 'Robert Johnson (Son)', phone: '(555) 234-5678', relationship: 'Son' },
      { name: 'Sarah Davis (Daughter)', phone: '(555) 345-6789', relationship: 'Daughter' },
      { name: 'Dr. Smith', phone: '(555) 456-7890', relationship: 'Primary Care Physician' }
    ],
    currentPlan: {
      name: 'Mobile Protection',
      price: '$39.99/month',
      features: ['GPS Tracking', 'Fall Detection', '24/7 Monitoring', 'Two-way Communication'],
      nextBilling: 'March 15, 2024'
    },
    deviceInfo: {
      model: 'Alert1 Mobile GPS',
      serialNumber: 'AM-2024-001234',
      batteryLevel: '85%',
      lastTest: 'February 28, 2024',
      status: 'Active'
    },
    recentActivity: [
      { date: '2024-02-28', type: 'System Test', description: 'Monthly system test completed successfully' },
      { date: '2024-02-15', type: 'Payment', description: 'Monthly payment processed - $39.99' },
      { date: '2024-02-10', type: 'Profile Update', description: 'Emergency contact information updated' },
      { date: '2024-01-28', type: 'System Test', description: 'Monthly system test completed successfully' }
    ]
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
  };

  const LoginForm = () => (
    <div className="login-container">
      <div className="login-card">
        <h1 className="sr-only">Sign In to Your Account</h1>
        <p className="account-access-notice" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff8e6', border: '1px solid #f0e6c8', borderRadius: '8px', textAlign: 'center', color: '#5c4a00', fontWeight: 500, fontSize: '1.2rem' }}>
          Thank you for being an Alert-1 customer! Account Access is temporarily unavailable while we upgrade our systems. Please call <strong><a href="tel:1-877-579-9862" style={{ color: 'inherit', textDecoration: 'underline' }}>1-877-579-9862</a></strong> and we will be happy to assist you with any account-related needs.
        </p>
        <div className="login-help">
          <h4>Need Help?</h4>
          <p>Call our customer service team at <strong>1-877-579-9862</strong> for assistance with your account.</p>
        </div>
      </div>
    </div>
  );

  const AccountDashboard = () => (
    <div className="account-dashboard">
      {/* Account Header */}
      <div className="account-header">
        <div className="user-welcome">
          <h1>Welcome back, {userData.name}!</h1>
          <p>Manage your Alert1 service and account settings</p>
        </div>
        <button onClick={handleLogout} className="btn btn-outline">
          Sign Out
        </button>
      </div>

      {/* Quick Status */}
      <div className="status-cards">
        <div className="status-card active">
          <div className="status-icon">✅</div>
          <div className="status-info">
            <h3>System Active</h3>
            <p>Your protection is active and monitoring</p>
          </div>
        </div>
        
        <div className="status-card">
          <div className="status-icon">🔋</div>
          <div className="status-info">
            <h3>Battery: {userData.deviceInfo.batteryLevel}</h3>
            <p>Good battery level</p>
          </div>
        </div>
        
        <div className="status-card">
          <div className="status-icon">📅</div>
          <div className="status-info">
            <h3>Next Billing</h3>
            <p>{userData.currentPlan.nextBilling}</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="account-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button 
          className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
        >
          Billing
        </button>
        <button 
          className={`tab ${activeTab === 'device' ? 'active' : ''}`}
          onClick={() => setActiveTab('device')}
        >
          Device
        </button>
        <button 
          className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          Activity
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="overview-grid">
              <div className="overview-card">
                <h3>Current Plan</h3>
                <div className="plan-info">
                  <h4>{userData.currentPlan.name}</h4>
                  <p className="plan-price">{userData.currentPlan.price}</p>
                  <ul className="plan-features">
                    {userData.currentPlan.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                  <button className="btn btn-outline">Change Plan</button>
                </div>
              </div>
              
              <div className="overview-card">
                <h3>Device Status</h3>
                <div className="device-status">
                  <div className="device-item">
                    <span>Model:</span>
                    <span>{userData.deviceInfo.model}</span>
                  </div>
                  <div className="device-item">
                    <span>Status:</span>
                    <span className="status-active">{userData.deviceInfo.status}</span>
                  </div>
                  <div className="device-item">
                    <span>Battery:</span>
                    <span>{userData.deviceInfo.batteryLevel}</span>
                  </div>
                  <div className="device-item">
                    <span>Last Test:</span>
                    <span>{userData.deviceInfo.lastTest}</span>
                  </div>
                  <button className="btn btn-primary">Test System</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="profile-content">
            <div className="profile-section">
              <h3>Personal Information</h3>
              <div className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input type="text" value={userData.name} readOnly />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" value={userData.email} readOnly />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input type="tel" value={userData.phone} readOnly />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input type="text" value={userData.address} readOnly />
                </div>
                <button className="btn btn-primary">Edit Information</button>
              </div>
            </div>
            
            <div className="profile-section">
              <h3>Emergency Contacts</h3>
              <div className="contacts-list">
                {userData.emergencyContacts.map((contact, index) => (
                  <div key={index} className="contact-item">
                    <div className="contact-info">
                      <h4>{contact.name}</h4>
                      <p>{contact.phone}</p>
                      <span className="contact-relationship">{contact.relationship}</span>
                    </div>
                    <button className="btn btn-outline btn-small">Edit</button>
                  </div>
                ))}
                <button className="btn btn-primary">Add Contact</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="billing-content">
            <div className="billing-overview">
              <h3>Billing Information</h3>
              <div className="billing-card">
                <div className="billing-item">
                  <span>Current Plan:</span>
                  <span>{userData.currentPlan.name}</span>
                </div>
                <div className="billing-item">
                  <span>Monthly Cost:</span>
                  <span>{userData.currentPlan.price}</span>
                </div>
                <div className="billing-item">
                  <span>Next Billing Date:</span>
                  <span>{userData.currentPlan.nextBilling}</span>
                </div>
                <div className="billing-item">
                  <span>Payment Method:</span>
                  <span>•••• •••• •••• 1234</span>
                </div>
              </div>
              <div className="billing-actions">
                <button className="btn btn-primary">Update Payment Method</button>
                <button className="btn btn-outline">Download Invoice</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'device' && (
          <div className="device-content">
            <div className="device-info-card">
              <h3>Device Information</h3>
              <div className="device-details">
                <div className="device-visual">
                  <svg width="150" height="150" viewBox="0 0 150 150" fill="none">
                    <rect x="50" y="30" width="50" height="90" rx="12" fill="#333" stroke="#555" strokeWidth="2"/>
                    <rect x="55" y="35" width="40" height="25" rx="4" fill="#1a1a1a"/>
                    <text x="75" y="50" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">ALERT1</text>
                    <circle cx="75" cy="80" r="15" fill="#d32f2f"/>
                    <circle cx="75" cy="80" r="10" fill="#b71c1c"/>
                    <text x="75" y="85" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">SOS</text>
                    <circle cx="75" cy="15" r="3" fill="#4caf50"/>
                  </svg>
                </div>
                <div className="device-specs">
                  <div className="spec-item">
                    <span>Model:</span>
                    <span>{userData.deviceInfo.model}</span>
                  </div>
                  <div className="spec-item">
                    <span>Serial Number:</span>
                    <span>{userData.deviceInfo.serialNumber}</span>
                  </div>
                  <div className="spec-item">
                    <span>Battery Level:</span>
                    <span className="battery-good">{userData.deviceInfo.batteryLevel}</span>
                  </div>
                  <div className="spec-item">
                    <span>Last Test:</span>
                    <span>{userData.deviceInfo.lastTest}</span>
                  </div>
                  <div className="spec-item">
                    <span>Status:</span>
                    <span className="status-active">{userData.deviceInfo.status}</span>
                  </div>
                </div>
              </div>
              <div className="device-actions">
                <button className="btn btn-primary">Test Device</button>
                <button className="btn btn-outline">Request Replacement</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="activity-content">
            <h3>Recent Activity</h3>
            <div className="activity-list">
              {userData.recentActivity.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-date">{activity.date}</div>
                  <div className="activity-details">
                    <h4>{activity.type}</h4>
                    <p>{activity.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="my-account-page">
      {/* Main Content */}
      <section className="account-content">
        <div className="container">
          {isLoggedIn ? <AccountDashboard /> : <LoginForm />}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MyAccountPage;
